<template>
    <div class="fp-page">
        <Toast />

        <div class="fp-hero">
            <div class="fp-hero-left">
                <div class="fp-title">Fatture Pratiche</div>
                <div class="fp-subtitle">Ricerca e consultazione fatture importate.</div>
            </div>

        </div>

        <div class="fp-card">
            <div class="fp-filters">
                <div class="fp-field">
                    <label class="fp-label">Sorgente</label>
                    <Dropdown
                        v-model="selectedSource"
                        :options="sources"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Seleziona sorgente"
                        class="fp-dropdown"
                        :disabled="rows.loading"
                    />
                </div>

                <div class="fp-field fp-field-small">
                    <label class="fp-label">Righe per pagina</label>
                    <Dropdown
                        v-model="rows.perPage"
                        :options="perPageOptions"
                        placeholder="Per pagina"
                        class="fp-dropdown"
                        :disabled="rows.loading"
                    />
                </div>

                <div class="fp-actions">
                    <Button
                        label="Cerca"
                        icon="pi pi-search"
                        class="fp-btn p-button-info"
                        :loading="rows.loading"
                        :disabled="!selectedSource"
                        @click="onSearch"
                    />

                    <Button
                        label="Reset"
                        icon="pi pi-refresh"
                        class="fp-btn p-button-info"
                        :disabled="rows.loading"
                        @click="onReset"
                    />
                </div>
            </div>

            <div class="fp-divider"></div>

            <div v-if="!searched" class="fp-empty">
                <div class="fp-empty-icon">
                    <i class="pi pi-database"></i>
                </div>
                <div class="fp-empty-title">Seleziona una sorgente e premi Cerca</div>
            </div>

            <div v-else class="fp-table-wrap">
                <div class="fp-table-header">
                    <div class="fp-table-title">
                        Risultati
                        <span class="fp-badge">{{ rows.meta.total }} righe</span>
                    </div>

                    <div class="fp-table-meta">
                        Pagina {{ rows.meta.page }} / {{ rows.meta.last_page }}
                        <span class="fp-dot">•</span>
                        In pagina {{ rows.data.length }}
                    </div>
                </div>

                <div class="fp-table-shell">
                    <div v-if="rows.loading" class="fp-section-loader">
                        <Loader />
                    </div>

                    <div class="fp-table-scroll">
                        <table class="fp-table">
                            <thead>
                                <tr>
                                    <th v-for="c in columns" :key="c.field">
                                        {{ c.label }}
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-if="rows.data.length === 0 && !rows.loading">
                                    <td class="fp-empty-td" :colspan="columns.length">
                                        Nessun record trovato.
                                    </td>
                                </tr>

                                <tr v-for="r in rows.data" :key="rowKey(r)">
                                    <td v-for="c in columns" :key="c.field" class="fp-cell">
                                        {{ formatCell(r, c.field) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="fp-pager-bar">
                        <div class="fp-pager-left">
                            <span class="fp-pager-text">
                                Totale: <b>{{ rows.meta.total }}</b>
                                • Pagina <b>{{ rows.meta.page }}</b> / <b>{{ rows.meta.last_page }}</b>
                            </span>
                        </div>

                        <div class="fp-pager">
                            <select
                                class="fp-perpage"
                                v-model.number="rows.perPage"
                                :disabled="rows.loading"
                                @change="goToPage(1)"
                            >
                                <option :value="10">10</option>
                                <option :value="25">25</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                                <option :value="200">200</option>
                            </select>

                            <button
                                class="p-button p-button-info fp-btn-lite"
                                type="button"
                                @click="prevPage"
                                :disabled="rows.loading || rows.meta.page <= 1"
                            >
                                Prev
                            </button>

                            <button
                                class="p-button p-button-info fp-btn-lite"
                                type="button"
                                @click="nextPage"
                                :disabled="rows.loading || rows.meta.page >= rows.meta.last_page"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div class="fp-footnote">
                    Suggerimento: usa Prev/Next e il selettore righe per pagina.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";

import Loader from "@/components/Loader.vue";
import FatturePraticheCeiService from "@/services/fatture-pratiche/fatture-pratiche-cei.js";

export default {
    name: "FatturePratichePage",
    components: { Dropdown, Button, Toast, Loader },
    data() {
        return {
            service: null,
            toast: null,

            sources: [{ label: "CEI", value: "cei" }],
            perPageOptions: [10, 25, 50, 100, 200],

            selectedSource: "cei",
            searched: false,

            rows: {
                loading: false,
                page: 1,
                perPage: 50,
                data: [],
                meta: { page: 1, per_page: 50, total: 0, last_page: 1 },
            },
        };
    },
    computed: {
        columns() {
            return [
            { field: "id", label: "id" },
            { field: "batch", label: "batch" },
            { field: "idsoc", label: "idsoc" },
            { field: "idfattura", label: "idfattura" },
            { field: "riga_id", label: "riga_id" },
            { field: "numeroutenza", label: "numeroutenza" },
            { field: "numeroutenzamubi", label: "numeroutenzamubi" },
            { field: "numeropuntoriconsegna", label: "numeropuntoriconsegna" },
            { field: "pod_id", label: "pod_id" },
            { field: "idcliente", label: "idcliente" },
            { field: "intestatario", label: "intestatario" },
            { field: "classeutenza", label: "classeutenza" },
            { field: "codcabinaremi", label: "codcabinaremi" },
            { field: "misuratore", label: "misuratore" },
            { field: "correttore", label: "correttore" },
            { field: "dataaperturautenza", label: "dataaperturautenza" },
            { field: "numerofattura", label: "numerofattura" },
            { field: "datafattura", label: "datafattura" },
            { field: "datainiziocompetenza", label: "datainiziocompetenza" },
            { field: "datafinecompetenza", label: "datafinecompetenza" },
            { field: "datascadenzafattura", label: "datascadenzafattura" },
            { field: "consumoreale", label: "consumoreale" },
            { field: "tipoacconto", label: "tipoacconto" },
            { field: "um", label: "um" },
            { field: "quantita", label: "quantita" },
            { field: "unitario", label: "unitario" },
            { field: "iva", label: "iva" },
            { field: "totaleimponibile", label: "totaleimponibile" },
            { field: "realeattuale", label: "realeattuale" },
            { field: "stimatoattuale", label: "stimatoattuale" },
            { field: "realeprecedente", label: "realeprecedente" },
            { field: "stimatoprecedente", label: "stimatoprecedente" },
            { field: "descrizionevoce", label: "descrizionevoce" },
            { field: "codofferta", label: "codofferta" },
            { field: "codtariffa", label: "codtariffa" },
            { field: "codvariante", label: "codvariante" },
            { field: "zonatariffa", label: "zonatariffa" },
            { field: "zonaaccisa", label: "zonaaccisa" },
            { field: "nomeregione", label: "nomeregione" },
            { field: "versionetariffa", label: "versionetariffa" },
            { field: "scaglionetariffa", label: "scaglionetariffa" },
            { field: "numeroarticolo", label: "numeroarticolo" },
            { field: "catregbusiness", label: "catregbusiness" },
            { field: "codiceiva", label: "codiceiva" },
            { field: "tipomercato", label: "tipomercato" },
            { field: "numerocontocg", label: "numerocontocg" },
            { field: "pcsapplicato", label: "pcsapplicato" },
            { field: "cumuloconsumi", label: "cumuloconsumi" },
            { field: "potenzaimpegnata", label: "potenzaimpegnata" },
            { field: "potenzadisponibile", label: "potenzadisponibile" },
            { field: "codufficioutf", label: "codufficioutf" },
            { field: "codiceprovincia", label: "codiceprovincia" },
            { field: "nomecomune", label: "nomecomune" },
            { field: "codicecatastale", label: "codicecatastale" },
            { field: "codicetariffa", label: "codicetariffa" },
            { field: "numeroscaglione", label: "numeroscaglione" },
            { field: "inizioscaglione", label: "inizioscaglione" },
            { field: "tiporicalcolo", label: "tiporicalcolo" },
            { field: "motivoricalcolo", label: "motivoricalcolo" },
            { field: "descrizioneparagrafo", label: "descrizioneparagrafo" },
            { field: "descrizionegruppo", label: "descrizionegruppo" },
            { field: "descrizionesezione", label: "descrizionesezione" },
            { field: "offerta", label: "offerta" },
            { field: "codicefatturazione", label: "codicefatturazione" },
            { field: "tensione", label: "tensione" },
            { field: "idmicroclasse", label: "idmicroclasse" },
            { field: "rif_cliente", label: "rif_cliente" },
            { field: "indirizzo", label: "indirizzo" },
            { field: "cap", label: "cap" },
            { field: "localita", label: "localita" },
            { field: "provincia", label: "provincia" },
            { field: "spread", label: "spread" },
            { field: "letturaattualecorrettore", label: "letturaattualecorrettore" },
            { field: "letturaprecedentecorrettore", label: "letturaprecedentecorrettore" },
            { field: "letturaattualecontatore", label: "letturaattualecontatore" },
            { field: "letturaprecedentecontatore", label: "letturaprecedentecontatore" },
            { field: "letturaattualepod", label: "letturaattualepod" },
            { field: "letturaprecedentepod", label: "letturaprecedentepod" },
            { field: "filename", label: "filename" },
            { field: "file_generated", label: "file_generated" },
            { field: "created_at", label: "created_at" },
            ];
        },
    },

    created() {
        this.service = new FatturePraticheCeiService();
        this.toast = useToast();
    },
    methods: {
        toastSuccess(detail, summary = "Operazione completata") {
            this.toast?.add({ severity: "success", summary, detail, life: 3500 });
        },
        toastError(detail, summary = "Errore") {
            this.toast?.add({ severity: "error", summary, detail, life: 4500 });
        },
        getBackendMessage(err, fallback) {
            return err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
        },

        rowKey(r) {
            return `cei-${r?.id ?? Math.random()}`;
        },

        async onSearch() {
            this.rows.page = 1;
            await this.loadRows();
        },

        async onReset() {
            this.selectedSource = "cei";
            this.searched = false;

            this.rows.page = 1;
            this.rows.perPage = 50;
            this.rows.data = [];
            this.rows.meta = { page: 1, per_page: 50, total: 0, last_page: 1 };
        },

        async loadRows() {
            if (this.selectedSource !== "cei") {
                this.toastError("Sorgente non valida.");
                return;
            }

            this.rows.loading = true;
            try {
                const res = await this.service.getRows({
                    page: this.rows.page,
                    per_page: this.rows.perPage,
                });

                const data = Array.isArray(res?.data) ? res.data : [];
                const meta = res?.meta || {};

                this.rows.data = data;

                this.rows.meta = {
                    page: Number(meta.current_page ?? this.rows.page),
                    per_page: Number(meta.per_page ?? this.rows.perPage),
                    total: Number(meta.total ?? 0),
                    last_page: Number(meta.last_page ?? 1),
                };

                this.rows.page = this.rows.meta.page;
                this.rows.perPage = this.rows.meta.per_page;

                this.searched = true;
            } catch (err) {
                this.rows.data = [];
                this.rows.meta = { page: 1, per_page: this.rows.perPage, total: 0, last_page: 1 };
                this.toastError(this.getBackendMessage(err, "Errore durante il caricamento dei record."));
            } finally {
                this.rows.loading = false;
            }
        },

        async goToPage(page) {
            this.rows.page = Math.max(1, page);
            await this.loadRows();
        },

        async prevPage() {
            if (this.rows.meta.page <= 1) return;
            await this.goToPage(this.rows.meta.page - 1);
        },

        async nextPage() {
            if (this.rows.meta.page >= this.rows.meta.last_page) return;
            await this.goToPage(this.rows.meta.page + 1);
        },

        formatCell(row, field) {
            const v = row ? row[field] : null;
            if (v === null || v === undefined) return "";

            if (this.isIsoDateOrDateTime(v)) {
                return this.formatDateIT(v);
            }

            if (this.isNumericLike(v)) {
                if (field === "id" || field === "idfattura" || field === "riga_id" || field === "pod_id") {
                    return v;
                }

                if (field === "quantita" || field === "unitario" || field === "totaleimponibile") {
                    return this.formatNumberIT(v);
                }

                return v;
            }

            return v;
        },

        isIsoDateOrDateTime(v) {
            if (typeof v !== "string") return false;
            return /^\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2}:\d{2})?$/.test(v.trim());
        },

        formatDateIT(v) {
            const s = String(v).trim();
            const datePart = s.slice(0, 10);
            const [y, m, d] = datePart.split("-").map((x) => parseInt(x, 10));
            if (!y || !m || !d) return v;

            const dd = String(d).padStart(2, "0");
            const mm = String(m).padStart(2, "0");
            return `${dd}/${mm}/${y}`;
        },

        isNumericLike(v) {
            if (typeof v === "number") return Number.isFinite(v);
            if (typeof v !== "string") return false;

            const s = v.trim();
            if (s === "") return false;

            const cleaned = s.replace(/\s+/g, "").replace(/[€]/g, "").replace(/\u00A0/g, "");
            return /^-?\d{1,3}([.,]\d{3})*([.,]\d+)?$/.test(cleaned) || /^-?\d+([.,]\d+)?$/.test(cleaned);
        },

        parseNumericToNumber(v) {
            if (typeof v === "number") return v;

            let s = String(v).trim();
            s = s.replace(/\s+/g, "").replace(/[€]/g, "").replace(/\u00A0/g, "");

            const hasDot = s.includes(".");
            const hasComma = s.includes(",");

            if (hasDot && hasComma) {
                const lastDot = s.lastIndexOf(".");
                const lastComma = s.lastIndexOf(",");
                const decimalSep = lastComma > lastDot ? "," : ".";

                if (decimalSep === ",") {
                    s = s.replace(/\./g, "");
                    s = s.replace(/,/g, ".");
                } else {
                    s = s.replace(/,/g, "");
                }
            } else if (hasComma && !hasDot) {
                s = s.replace(/,/g, ".");
            }

            const n = Number(s);
            return Number.isFinite(n) ? n : null;
        },

        formatNumberIT(v) {
            const n = this.parseNumericToNumber(v);
            if (n === null) return v;

            return new Intl.NumberFormat("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(n);
        },
    },
};
</script>

<style scoped>
/* ===== Layout "come prima" ===== */
.fp-page {
    padding: 18px !important;
    max-width: 1400px !important;
    margin: 0 auto !important;
}

.fp-hero {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 14px !important;
    margin-bottom: 14px !important;
}

.fp-title {
    font-size: 26px !important;
    font-weight: 800 !important;
    letter-spacing: -0.3px !important;
    line-height: 1.1 !important;
}

.fp-subtitle {
    margin-top: 6px !important;
    opacity: 0.8 !important;
    font-size: 14px !important;
}

.fp-pill {
    display: inline-flex !important;
    align-items: center !important;
    gap: 10px !important;
    padding: 10px 12px !important;
    border-radius: 14px !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid rgba(255, 255, 255, 0.10) !important;
    backdrop-filter: blur(8px) !important;
}

.fp-pill-icon {
    font-size: 16px !important;
}

.fp-pill-text {
    font-weight: 700 !important;
    font-size: 13px !important;
    opacity: 0.9 !important;
}

.fp-card {
    border-radius: 16px !important;
    padding: 14px !important;
    background: rgba(255, 255, 255, 0.04) !important;
    border: 1px solid rgba(255, 255, 255, 0.10) !important;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25) !important;
}

.fp-filters {
    display: grid !important;
    grid-template-columns: 1.2fr 0.6fr auto !important;
    gap: 12px !important;
    align-items: end !important;
}

.fp-field {
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
}

.fp-field-small {
    min-width: 180px !important;
}

.fp-label {
    font-size: 12px !important;
    opacity: 0.85 !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
}

.fp-actions {
    display: flex !important;
    gap: 10px !important;
    justify-content: flex-end !important;
    flex-wrap: wrap !important;
}

.fp-btn {
    border-radius: 12px !important;
    padding: 10px 14px !important;
    font-weight: 800 !important;
}

.fp-btn-primary {
    background: rgba(255, 255, 255, 0.12) !important;
    border: 1px solid rgba(255, 255, 255, 0.18) !important;
}

.fp-btn-ghost {
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.14) !important;
}

.fp-divider {
    height: 1px !important;
    margin: 14px 0 !important;
    background: rgba(255, 255, 255, 0.10) !important;
}

/* empty */
.fp-empty {
    padding: 26px 10px !important;
    text-align: center !important;
}

.fp-empty-icon {
    width: 52px !important;
    height: 52px !important;
    border-radius: 14px !important;
    margin: 0 auto 10px auto !important;
    display: grid !important;
    place-items: center !important;
    background: rgba(255, 255, 255, 0.07) !important;
    border: 1px solid rgba(255, 255, 255, 0.10) !important;
}

.fp-empty-icon i {
    font-size: 22px !important;
}

.fp-empty-title {
    font-weight: 900 !important;
    margin-top: 8px !important;
    font-size: 15px !important;
}

.fp-empty-text {
    opacity: 0.75 !important;
    margin-top: 6px !important;
    font-size: 13px !important;
}

/* header sopra tabella */
.fp-table-header {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 10px !important;
    margin-bottom: 10px !important;
}

.fp-table-title {
    font-weight: 900 !important;
    font-size: 14px !important;
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
}

.fp-badge {
    display: inline-flex !important;
    align-items: center !important;
    padding: 4px 10px !important;
    border-radius: 999px !important;
    font-size: 12px !important;
    font-weight: 800 !important;
    background: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.fp-table-meta {
    opacity: 0.75 !important;
    font-size: 12px !important;
    font-weight: 700 !important;
}

.fp-dot {
    margin: 0 8px !important;
    opacity: 0.6 !important;
}

.fp-footnote {
    margin-top: 10px !important;
    font-size: 12px !important;
    opacity: 0.7 !important;
}

/* ===== Tabella "come ora" (stile tipo TS) ===== */
.fp-table-shell {
    position: relative !important;
}

.fp-section-loader {
    position: absolute !important;
    inset: 0 !important;
    background: rgba(255, 255, 255, 0.75) !important;
    display: grid !important;
    place-items: center !important;
    z-index: 50 !important;
    border-radius: 12px !important;
}

.fp-table-scroll {
    overflow: auto !important;
    border-radius: 12px !important;
    border: 2px solid rgba(17, 24, 39, 0.16) !important;
    background: #fff !important;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06) !important;
}

.fp-table {
    width: 100% !important;
    border-collapse: collapse !important;
    font-size: 13px !important;
    background: #fff !important;
}

.fp-table thead th {
    background: #068de0 !important;
    color: #fff !important;
    font-weight: 800 !important;
    font-size: 12px !important;
    padding: 10px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.02em !important;
    border-right: 1px solid rgba(255, 255, 255, 0.25) !important;
    user-select: none !important;
    white-space: nowrap !important;
}

.fp-table thead th:last-child {
    border-right: none !important;
}

.fp-table td {
    padding: 10px !important;
    border-bottom: 1px solid rgba(17, 24, 39, 0.14) !important;
    vertical-align: top !important;
}

.fp-table tbody tr:hover td {
    background: rgba(6, 141, 224, 0.07) !important;
}

.fp-empty-td {
    text-align: center !important;
    padding: 16px !important;
    opacity: 0.85 !important;
}

.fp-pager-bar {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    gap: 10px !important;
    padding: 12px 14px !important;
    border-top: 1px solid rgba(17, 24, 39, 0.12) !important;
    background: #fff !important;
    border-radius: 0 0 12px 12px !important;
}

.fp-pager {
    display: flex !important;
    gap: 8px !important;
    align-items: center !important;
}

.fp-pager-text {
    font-size: 13px !important;
    opacity: 0.9 !important;
}

.fp-perpage {
    height: 34px !important;
    border-radius: 10px !important;
    border: 1px solid rgba(17, 24, 39, 0.22) !important;
    padding: 0 8px !important;
    background: #fff !important;
}

.fp-btn-lite {
    border-radius: 10px !important;
    padding: 0.45rem 0.75rem !important;
    font-size: 0.85rem !important;
}

:deep(.fp-dropdown .p-dropdown) {
    width: 100% !important;
}

@media (max-width: 900px) {
    .fp-hero {
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    .fp-filters {
        grid-template-columns: 1fr !important;
    }

    .fp-actions {
        justify-content: flex-start !important;
    }

    .fp-pager-bar {
        flex-direction: column !important;
        align-items: stretch !important;
    }

    .fp-pager {
        justify-content: flex-end !important;
    }
}
</style>